import { Component, JSX } from "react";

import { ProjectService } from "@app/projects";
import { AdequacyAggregate } from "@app/projects/network-assessment/adequacy";
import { RegionGranularity, RegionSet } from "@app/region-sets";
import { FilterItemType } from "@app/resource-filters/common";
import { FilterItem } from "@app/resource-filters/filter-items";
import { FilteredResource, ResourceStatus } from "@app/resources";
import { QESButton } from "@app/shared/qes-standards";
import { ColumnConfig, numberSort, SortDirections, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { FlyoutContextOutgoingProps, withFlyoutContext } from "@app/shared/Flyouts";

type RegionTableProps = FlyoutContextOutgoingProps & {
  filteredResources: FilteredResource[];
  handleAddFilter: (newFilterItem: FilterItem) => void;
  showingArchived: boolean;
}

interface RegionTableState {
  loading: boolean;
  regionSetAggregates: AdequacyAggregate[];
  sortedRecords: RegionTableData[];
}

interface DashboardRegion {
  filterUrl: string;
  key: string;
  numberOfProjects: number;
  numberOfGaps: number;
  percentageOfGaps: number;
}

interface RegionTableData {
  [wafflesKeyColumnName]: string;
  county: JSX.Element;
  numberOfProjects: string;
  numberOfGaps: string;
  percentageOfGaps: string;
}

class WrappedRegionTable extends Component<RegionTableProps, RegionTableState> {
  private readonly projectService = new ProjectService();

  private columnConfig: ColumnConfig = {
    county: {
      displayText: "County",
      testId: "county",
    },
    numberOfGaps: {
      displayText: "Gaps #",
      testId: "number-of-gaps",
    },
    numberOfProjects: {
      displayText: "Projects #",
      testId: "number-of-projects",
    },
    percentageOfGaps: {
      displayText: "Gaps %",
      testId: "percentage-of-gaps",
    },
  };

  constructor(props: RegionTableProps) {
    super(props);

    this.state = {
      loading: true,
      regionSetAggregates: null,
      sortedRecords: [],
    };
  }

  public async componentDidMount() {
    const regionSetAggregates = await this.projectService.getRegionSetAggregates(this.props.filteredResources, this.props.showingArchived ? ResourceStatus.Archived : ResourceStatus.Active);
    const newRegionData: DashboardRegion[] = regionSetAggregates.map((row) => ({
      filterUrl: row.regionGranularity === RegionGranularity.State ? `\u00A7ServiceAreas\u00A7States\u00A7${row.stateDisplayName}` : `\u00A7ServiceAreas\u00A7Counties\u00A7${row.stateDisplayName} Counties\u00A7${row.regionSetDisplayName}`,
      key: `${row.regionSetDisplayName}, ${row.stateDisplayName}`,
      numberOfGaps: row.gapCount,
      numberOfProjects: row.projectCount,
      percentageOfGaps: row.percentGaps * 100,
    }));

    newRegionData.sort((a, b) => {
      if (a.percentageOfGaps === b.percentageOfGaps) {
        if (a.key > b.key) {
          return 1;
        } else {
          return -1;
        }
      } else if (a.percentageOfGaps < b.percentageOfGaps) {
        return 1;
      } else {
        return -1;
      }
    });

    const rowClickable =
      this.props.filteredResources &&
      !this.props.filteredResources.some((fr) => fr.serviceAreaRegionSets && fr.serviceAreaRegionSets.some((rs) => this.hasCountyRegions(rs)));

    /* eslint-disable sort-keys */
    const tableData: RegionTableData[] = newRegionData.map((data) => ({
      [wafflesKeyColumnName]: data.key,
      county: rowClickable ? <QESButton qesType="secondary" qesTestId={`region-table-add-filter--${data.key}`} key={data.key} onClick={() => this.handleAddCountyFilter(data.filterUrl)}>{data.key}</QESButton> : <span key={data.key}>{data.key}</span>,
      numberOfProjects: data.numberOfProjects.toLocaleString("en-US"),
      numberOfGaps: data.numberOfGaps.toLocaleString("en-US"),
      percentageOfGaps: isNaN(data.percentageOfGaps) ? "---" : `${data.percentageOfGaps.toFixed(1)}%`,
    }));
    /* eslint-enable sort-keys */

    this.setState({
      loading: false,
      regionSetAggregates,
      sortedRecords: tableData,
    });
  }

  public render() {
    return (
      <>
        <Waffles<RegionTableData>
          columns={this.columnConfig}
          customSort={this.customSort}
          data={this.state.sortedRecords}
          loading={this.state.loading}
          testId="dashboard-region-table"
        />
      </>
    );
  }

  private hasCountyRegions = (region: RegionSet): boolean => {
    if (region.granularity === RegionGranularity.County) {
      return true;
    }

    let foundCountyGranularity = false;
    if (region.partialRegionSets) {
      for (const subRegion of region.partialRegionSets) {
        foundCountyGranularity = this.hasCountyRegions(subRegion);
        if (foundCountyGranularity) {
          break;
        }
      }
    }

    return foundCountyGranularity;
  }

  private handleAddCountyFilter = (filterUrl: string) => {
    const newFilterItem: FilterItem = {
      displayName: "Item",
      itemType: FilterItemType.Leaf,
      itemValue: "Value",
      namespacePath: filterUrl,
    };
    this.props.handleCloseFlyout().then(() => {
      this.props.handleAddFilter(newFilterItem);
    });
  }

  private customSort = (data: RegionTableData[], columnKey: keyof RegionTableData, sortDirection: SortDirections) => {
    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

    switch (columnKey) {
      case "county":
        return data.sort((a, b) => (a[columnKey].key > b[columnKey].key ? 1 : -1) * multiplier);
      case "numberOfGaps":
      case "numberOfProjects":
      case "percentageOfGaps":
        return numberSort(data, columnKey, multiplier);
    }
  }
}

const RegionTable = withFlyoutContext(WrappedRegionTable);
export default RegionTable;
